import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import useBrandPayoutReportsFeatureFlag from 'modules/brand/hooks/useBrandPayoutReportsFeatureFlag';
import { useIsVenueGroupEnabled } from 'modules/brand/hooks/useIsVenueGroupEnabled';
import { getBrandRoute } from 'modules/brand/utils/getBrandRoute';
import useInsightV2FeatureFlag from 'modules/insights-v2/hooks/useInsightV2FeatureFlag';
import { useIsSettingsPageEnabled } from 'modules/shared-menu/hooks/useIsSettingsPageEnabled';
import { useIsBrandSharedMenuEnabled } from 'modules/shared-menu/hooks/useIsSharedMenuEnabled';

import { AvailableRouteItem } from './types';

type UseBrandAvailableRoutesProps = {
  brandId: string;
  enterpriseId: string;
  brandCountry: string;
};

export const useBrandAvailableRoutes = ({ brandId, enterpriseId, brandCountry }: UseBrandAvailableRoutesProps) => {
  const intl = useIntl();
  const isVenueGroupEnabled = useIsVenueGroupEnabled(brandCountry, brandId);

  const isSharedMenuEnabled = useIsBrandSharedMenuEnabled(brandId);
  const isSharedMenuSettingsPageEnabled = useIsSettingsPageEnabled();
  const isBrandPayoutReportsEnabled = useBrandPayoutReportsFeatureFlag(brandId);
  const { isInsightV2Enabled } = useInsightV2FeatureFlag('brand', brandId);
  const items: AvailableRouteItem[] = useMemo(() => {
    const t = (id: string) => intl.formatMessage({ id });

    const insightsSubItems = [
      {
        label: t('insights.performance.header'),
        link: `/enterprise/${enterpriseId}/brand/${brandId}/insights/performance`,
        type: 'INSIGHTS_V2_PERFORMANCE' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_PERFORMANCE' as const,
      },
      {
        label: t('insights.operations.header'),
        link: `/enterprise/${enterpriseId}/brand/${brandId}/insights/operations`,
        type: 'INSIGHTS_V2_OPERATIONS' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_OPERATIONS' as const,
      },
      {
        label: t('insights.customer-base.header'),
        link: `/enterprise/${enterpriseId}/brand/${brandId}/insights/customer-base`,
        type: 'INSIGHTS_V2_CUSTOMER_BASE' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_CUSTOMER_BASE' as const,
      },
      {
        label: t('insights.menu.header'),
        link: `/enterprise/${enterpriseId}/brand/${brandId}/insights/menu`,
        type: 'INSIGHTS_V2_MENU' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_MENU' as const,
      },
      {
        label: t('insights.ratings-and-reviews.header'),
        link: `/enterprise/${enterpriseId}/brand/${brandId}/insights/ratings`,
        type: 'INSIGHTS_V2_RATINGS_AND_REVIEWS' as const,
        trackingKey: 'OPEN_INSIGHTS_V2_RATINGS_AND_REVIEWS' as const,
      },
    ];

    const venuesSubItems = [
      ...(isVenueGroupEnabled
        ? [
            {
              label: t('navigation.venue-groups'),
              link: getBrandRoute('venue-groups', enterpriseId, brandId),
              type: 'BRAND_VENUE_GROUPS' as const,
            },
          ]
        : []),

      {
        label: t('navigation.historical-venue-availability'),
        link: getBrandRoute('historical-venue-availability', enterpriseId, brandId),
        type: 'HISTORICAL_VENUE_AVAILABILITY' as const,
      },
    ];

    const reportingSubItems = [
      ...(isBrandPayoutReportsEnabled
        ? [
            {
              label: t('navigation.payout-reports'),
              link: getBrandRoute('payout', enterpriseId, brandId),
              type: 'BRAND_PAYOUT_REPORTS' as const,
            },
          ]
        : []),
    ];

    return [
      {
        exact: true,
        label: t('navigation.purchase-data'),
        link: getBrandRoute('purchase-data', enterpriseId, brandId),
        type: 'PURCHASES',
      },
      ...(isInsightV2Enabled
        ? [
            {
              label: t('navigation.insights'),
              link: getBrandRoute('insights', enterpriseId, brandId),
              type: 'BRAND_INSIGHTS' as const,
              subItems: insightsSubItems,
            },
          ]
        : []),

      {
        label: t('navigation.venues'),
        link: getBrandRoute('venues', enterpriseId, brandId),
        type: 'VENUES' as const,
        subItems: venuesSubItems,
      },
      {
        label: t('navigation.reporting'),
        link: getBrandRoute('reporting', enterpriseId, brandId),
        type: 'BRAND_REPORTING' as const,
        subItems: reportingSubItems,
      },
      {
        label: t('navigation.access-management'),
        link: getBrandRoute('access', enterpriseId, brandId),
        type: 'ACCESS_MANAGEMENT' as const,
      },
      ...(isSharedMenuEnabled
        ? [
            {
              label: t('listing-manager.title'),
              link: getBrandRoute('shared-menu', enterpriseId, brandId),
              type: 'SHARED_MENU' as const,
              subItems: isSharedMenuSettingsPageEnabled
                ? [
                    {
                      label: t('listing-manager.settings.title'),
                      link: `${getBrandRoute('shared-menu', enterpriseId, brandId)}/settings`,
                      type: 'SHARED_MENU_SETTINGS' as const,
                    },
                  ]
                : [],
            },
          ]
        : []),
    ];
  }, [
    enterpriseId,
    brandId,
    isInsightV2Enabled,
    isBrandPayoutReportsEnabled,
    isSharedMenuEnabled,
    isSharedMenuSettingsPageEnabled,
    isVenueGroupEnabled,
    intl,
  ]);

  return { items };
};
